import {
  gmShopEffectivePrice,
  gmShopListPrice,
  GM_SHOP_CURRENCY_LABELS,
  GM_SHOP_FULFILLMENT_LABELS,
  listGmShopItemsForPlayers,
  maxAffordableShopQuantity,
  type GmShopItem,
} from "@/gm/gm-shop-catalog";
import { HOME_ASSETS } from "@/home/home-assets";
import { homeIcon } from "@/home/home-icons";
import { renderDungeonMonsterShopPanel } from "@/dungeon/dungeon-monster-shop-panel";
import type { DungeonShopConfig } from "@/dungeon/dungeon-shop-types";
import type { GmMonsterTemplate } from "@/gm/gm-monster-catalog";
import { slotLabelTh, type HomeEconomy } from "@/home/economy";
import { HOME_PART_KINDS, type HomePartKind } from "@/home/part-kind";

export type CurrencyShopTab = "coins" | "gems" | "cash" | "monsters";

/** Fills ~2–4 rows depending on viewport column count (5–10). */
export const SHOP_ITEMS_PER_PAGE = 20;

export type CurrencyShopPanelRender = {
  bodyHtml: string;
  paginationHtml: string;
  page: number;
  totalPages: number;
};

function currencyShopItemsInnerHtml(render: CurrencyShopPanelRender): string {
  return `${render.bodyHtml}${render.paginationHtml}`;
}

export type ShopPageByTab = Record<CurrencyShopTab, number>;

export function defaultShopPageByTab(): ShopPageByTab {
  return { coins: 0, gems: 0, cash: 0, monsters: 0 };
}

export function parseCurrencyShopTab(value: string | null): CurrencyShopTab | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === "coins" || v === "coin" || v === "gold") return "coins";
  if (v === "gems" || v === "gem" || v === "diamond" || v === "dimon") return "gems";
  if (v === "cash" || v === "revive") return "cash";
  if (v === "monsters" || v === "monster" || v === "dungeon") return "monsters";
  return null;
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function priceText(item: GmShopItem): string {
  const list = gmShopListPrice(item);
  const effective = gmShopEffectivePrice(item);
  const cur = GM_SHOP_CURRENCY_LABELS[item.currency];
  if (item.discountPercent > 0 && effective < list) {
    return `${effective.toLocaleString("en-US")} ${cur}`;
  }
  return `${effective.toLocaleString("en-US")} ${cur}`;
}

function priceHtml(item: GmShopItem): string {
  const list = gmShopListPrice(item);
  const effective = gmShopEffectivePrice(item);
  const cur = GM_SHOP_CURRENCY_LABELS[item.currency];
  if (item.discountPercent > 0 && effective < list) {
    return `<span class="home-shop-price-was">${list.toLocaleString("en-US")}</span> <strong>${effective.toLocaleString("en-US")}</strong> ${escapeHtml(cur)}`;
  }
  return `<strong>${effective.toLocaleString("en-US")}</strong> ${escapeHtml(cur)}`;
}

function paginate<T>(items: T[], page: number): { slice: T[]; page: number; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / SHOP_ITEMS_PER_PAGE));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const start = safePage * SHOP_ITEMS_PER_PAGE;
  return {
    slice: items.slice(start, start + SHOP_ITEMS_PER_PAGE),
    page: safePage,
    totalPages,
  };
}

export function shopPageForItem(tab: CurrencyShopTab, itemId: string): number {
  if (tab === "monsters") return 0;
  const items = listGmShopItemsForPlayers(tab);
  const index = items.findIndex((item) => item.id === itemId);
  if (index < 0) return 0;
  return Math.floor(index / SHOP_ITEMS_PER_PAGE);
}

function renderShopGridTile(item: GmShopItem): string {
  const discount =
    item.discountPercent > 0
      ? `<span class="home-shop-grid-tile-discount">−${item.discountPercent}%</span>`
      : "";
  return `
    <button type="button" class="home-shop-grid-tile" data-shop-open-detail="${escapeHtml(item.id)}" aria-label="${escapeHtml(item.name)} · ${escapeHtml(priceText(item))}">
      <span class="home-shop-grid-tile-art">
        <img src="${escapeHtml(item.imageDataUrl)}" alt="" width="56" height="56" loading="lazy" />
        ${discount}
      </span>
      <span class="home-shop-grid-tile-meta">
        <span class="home-shop-grid-tile-price">${escapeHtml(priceText(item))}</span>
        <span class="home-shop-grid-tile-name">${escapeHtml(item.name)}</span>
      </span>
    </button>`;
}

function renderPagination(tab: CurrencyShopTab, page: number, totalPages: number): string {
  if (totalPages <= 1) {
    return `<nav class="home-shop-pagination home-shop-pagination--solo" aria-label="หน้ารายการสินค้า"><span class="hint">หน้า 1/1</span></nav>`;
  }
  const prevDisabled = page <= 0 ? " disabled" : "";
  const nextDisabled = page >= totalPages - 1 ? " disabled" : "";
  return `
    <nav class="home-shop-pagination" aria-label="หน้ารายการสินค้า">
      <button type="button" class="home-shop-page-btn" data-shop-page-prev data-shop-page-tab="${tab}"${prevDisabled} aria-label="หน้าก่อน">${homeIcon("back")}</button>
      <span class="home-shop-page-label">${page + 1} / ${totalPages}</span>
      <button type="button" class="home-shop-page-btn" data-shop-page-next data-shop-page-tab="${tab}"${nextDisabled} aria-label="หน้าถัดไป">${homeIcon("play")}</button>
    </nav>`;
}

export function renderCurrencyShopPanelItems(
  tab: CurrencyShopTab,
  page = 0,
): CurrencyShopPanelRender {
  if (tab === "monsters") {
    return {
      bodyHtml: `<p class="hint home-shop-empty">เลือกแท็บมอนสเตอร์</p>`,
      paginationHtml: "",
      page: 0,
      totalPages: 1,
    };
  }
  const items = listGmShopItemsForPlayers(tab);
  if (items.length === 0) {
    return {
      bodyHtml: `<p class="hint home-shop-empty">ยังไม่มีสินค้าในหมวดนี้</p>`,
      paginationHtml: "",
      page: 0,
      totalPages: 1,
    };
  }
  const { slice, page: safePage, totalPages } = paginate(items, page);
  const bodyHtml = `<div class="home-shop-grid-scroll"><div class="home-shop-grid" role="list">${slice.map(renderShopGridTile).join("")}</div></div>`;
  const paginationHtml = renderPagination(tab, safePage, totalPages);
  return { bodyHtml, paginationHtml, page: safePage, totalPages };
}

function walletLine(tab: CurrencyShopTab, economy: HomeEconomy): string {
  if (tab === "coins") {
    return `ทองของคุณ: <strong id="currency-shop-wallet-coins">${economy.wallet.coins.toLocaleString("en-US")}</strong>`;
  }
  if (tab === "gems") {
    return `เพชรของคุณ: <strong id="currency-shop-wallet-gems">${economy.wallet.gems.toLocaleString("en-US")}</strong>`;
  }
  return `Cash ของคุณ: <strong id="cash-shop-wallet-cash">${economy.wallet.cash.toLocaleString("en-US")}</strong>`;
}

function catalogKindOptions(selected: HomePartKind): string {
  return HOME_PART_KINDS.map((kind) => {
    const sel = kind === selected ? " selected" : "";
    return `<option value="${kind}"${sel}>${slotLabelTh(kind)}</option>`;
  }).join("");
}

export type ShopDetailRenderOptions = {
  item: GmShopItem;
  economy: HomeEconomy;
  quantity: number;
  catalogPartKind: HomePartKind;
};

export function renderShopDetailBody(options: ShopDetailRenderOptions): string {
  const { item, economy, quantity, catalogPartKind } = options;
  const unit = gmShopEffectivePrice(item);
  const total = unit * quantity;
  const cur = GM_SHOP_CURRENCY_LABELS[item.currency];
  const maxQty = maxAffordableShopQuantity(economy, item);
  const canBuy = maxQty >= quantity && quantity >= 1;
  const kindBlock =
    item.fulfillment === "catalog_slot_pack"
      ? `<label class="home-currency-shop-kind">
          <span class="field-label">หมวดคลัง</span>
          <select class="text-input" id="home-shop-detail-kind">${catalogKindOptions(catalogPartKind)}</select>
        </label>`
      : "";

  return `
    <div class="home-shop-detail-layout">
      <div class="home-shop-detail-art">
        <img src="${escapeHtml(item.imageDataUrl)}" alt="" width="140" height="140" />
        ${item.discountPercent > 0 ? `<span class="home-shop-discount-badge">−${item.discountPercent}%</span>` : ""}
      </div>
      <div class="home-shop-detail-body">
        <h3 class="home-shop-detail-title">${escapeHtml(item.name)}</h3>
        <p class="home-shop-detail-price">${priceHtml(item)} <span class="hint">/ ชิ้น</span></p>
        <p class="hint home-shop-detail-fulfillment">${escapeHtml(GM_SHOP_FULFILLMENT_LABELS[item.fulfillment])}</p>
        ${item.description ? `<p class="home-shop-detail-desc">${escapeHtml(item.description)}</p>` : ""}
        ${kindBlock}
        <div class="home-shop-qty-row" role="group" aria-label="จำนวน">
          <span class="field-label">จำนวน</span>
          <div class="home-shop-qty-controls">
            <button type="button" class="home-shop-qty-btn" data-shop-qty-minus aria-label="ลดจำนวน">−</button>
            <input type="number" class="text-input home-shop-qty-input" id="home-shop-detail-qty" min="1" max="${maxQty > 0 ? maxQty : 1}" value="${quantity}" inputmode="numeric" />
            <button type="button" class="home-shop-qty-btn" data-shop-qty-plus aria-label="เพิ่มจำนวน 1">+</button>
            <button type="button" class="home-shop-qty-btn home-shop-qty-btn--bulk" data-shop-qty-bulk aria-label="เพิ่มจำนวน 99">+99</button>
          </div>
          <p class="hint home-shop-qty-hint">ซื้อได้สูงสุด ${Math.max(0, maxQty).toLocaleString("en-US")} ชิ้น (ตามยอด${escapeHtml(cur)})</p>
        </div>
        <p class="home-shop-detail-total">รวม <strong id="home-shop-detail-total">${total.toLocaleString("en-US")}</strong> ${escapeHtml(cur)}</p>
        <button type="button" class="btn btn-primary home-shop-detail-buy" data-shop-detail-buy="${escapeHtml(item.id)}" ${canBuy ? "" : "disabled"}>ซื้อ</button>
      </div>
    </div>`;
}

export function currencyShopModalMarkup(
  economy: HomeEconomy,
  activeTab: CurrencyShopTab = "cash",
  pages: ShopPageByTab = defaultShopPageByTab(),
  dungeonMonsters?: { shop: DungeonShopConfig; catalog: GmMonsterTemplate[] },
): string {
  const tabBtn = (id: CurrencyShopTab, label: string, iconSrc?: string) => {
    const active = id === activeTab;
    const icon = iconSrc
      ? `<img class="home-currency-shop-tab-icon" src="${iconSrc}" alt="" width="18" height="18" />`
      : `<span class="home-currency-shop-tab-mark" aria-hidden="true">$</span>`;
    return `<button type="button" class="home-currency-shop-tab${active ? " home-currency-shop-tab--active" : ""}" role="tab" data-currency-shop-tab="${id}" aria-selected="${active ? "true" : "false"}">${icon}<span>${label}</span></button>`;
  };

  const panel = (tab: CurrencyShopTab) => {
    if (tab === "monsters") {
      const html =
        dungeonMonsters
          ? renderDungeonMonsterShopPanel(economy, dungeonMonsters.shop, dungeonMonsters.catalog)
          : `<p class="hint home-shop-empty">กำลังโหลดมอนสเตอร์…</p>`;
      return `
        <section class="home-currency-shop-panel" data-currency-shop-panel="monsters"${activeTab === "monsters" ? "" : " hidden"}>
          <div class="home-currency-shop-items home-currency-shop-items--monsters" id="currency-shop-items-monsters">
            ${html}
          </div>
        </section>`;
    }
    const rendered = renderCurrencyShopPanelItems(tab, pages[tab]);
    return `
        <section class="home-currency-shop-panel" data-currency-shop-panel="${tab}"${activeTab === tab ? "" : " hidden"}>
          <p class="hint">${walletLine(tab, economy)}</p>
          <div class="home-currency-shop-items" id="currency-shop-items-${tab}" data-shop-page="${rendered.page}">
            ${currencyShopItemsInnerHtml(rendered)}
          </div>
        </section>`;
  };

  return `
    <div id="home-currency-shop-modal" class="home-cash-shop-modal home-currency-shop-modal" hidden aria-hidden="true">
      <button type="button" class="home-cash-shop-backdrop" data-close-currency-shop aria-label="ปิดร้าน"></button>
      <div class="home-cash-shop-dialog home-currency-shop-dialog home-currency-shop-dialog--rpg" role="dialog" aria-modal="true" aria-labelledby="home-currency-shop-title">
        <header class="home-cash-shop-head home-currency-shop-head-rpg">
          <button type="button" class="dungeon-frame-close home-currency-shop-close" data-close-currency-shop aria-label="ปิด">×</button>
          <div class="home-currency-shop-title-plaque">
            <span class="home-currency-shop-title-gem" aria-hidden="true"></span>
            <div class="home-cash-shop-head-text">
              <p class="dungeon-frame-kicker home-currency-shop-kicker">ร้านค้า</p>
              <h2 id="home-currency-shop-title" class="dungeon-frame-title home-currency-shop-title">Shop</h2>
            </div>
          </div>
        </header>
        <div class="home-currency-shop-tabs" role="tablist" aria-label="สกุลเงิน">
          ${tabBtn("coins", "ทอง", HOME_ASSETS.coin)}
          ${tabBtn("gems", "เพชร", HOME_ASSETS.gem)}
          ${tabBtn("cash", "Cash")}
          ${tabBtn("monsters", "มอนสเตอร์")}
        </div>
        ${panel("coins")}
        ${panel("gems")}
        ${panel("cash")}
        ${panel("monsters")}
      </div>
      <div id="home-shop-detail-modal" class="home-shop-detail-modal" hidden aria-hidden="true">
        <button type="button" class="home-shop-detail-backdrop" data-close-shop-detail aria-label="ปิดรายละเอียด"></button>
        <div class="home-shop-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="home-shop-detail-title">
          <header class="home-shop-detail-head">
            <button type="button" class="btn btn-ghost btn-icon home-shop-detail-back" data-close-shop-detail aria-label="กลับ">
              ${homeIcon("back")}
            </button>
            <h2 id="home-shop-detail-title" class="home-shop-detail-head-title">รายละเอียดสินค้า</h2>
            <button type="button" class="btn btn-ghost btn-icon" data-close-shop-detail aria-label="ปิด">
              ${homeIcon("close")}
            </button>
          </header>
          <div class="home-shop-detail-content" id="home-shop-detail-content"></div>
        </div>
      </div>
    </div>`;
}

export function gemKindFromSelect(
  select: HTMLSelectElement | null,
  fallback: HomePartKind,
): HomePartKind {
  const value = select?.value;
  if (value && (HOME_PART_KINDS as readonly string[]).includes(value)) {
    return value as HomePartKind;
  }
  return fallback;
}

export function refreshCurrencyShopPanelDom(
  root: ParentNode,
  economy: HomeEconomy,
  pages: ShopPageByTab,
  dungeonMonsters?: { shop: DungeonShopConfig; catalog: GmMonsterTemplate[] },
): ShopPageByTab {
  const nextPages = { ...pages };
  if (dungeonMonsters) {
    const host = root.querySelector<HTMLElement>("#currency-shop-items-monsters");
    if (host) {
      host.innerHTML = renderDungeonMonsterShopPanel(
        economy,
        dungeonMonsters.shop,
        dungeonMonsters.catalog,
      );
    }
  }
  for (const tab of ["coins", "gems", "cash"] as CurrencyShopTab[]) {
    const host = root.querySelector<HTMLElement>(`#currency-shop-items-${tab}`);
    if (!host) continue;
    const rendered = renderCurrencyShopPanelItems(tab, pages[tab]);
    host.innerHTML = currencyShopItemsInnerHtml(rendered);
    host.dataset.shopPage = String(rendered.page);
    nextPages[tab] = rendered.page;
  }
  const walletCoins = root.querySelector<HTMLElement>("#currency-shop-wallet-coins");
  const walletGems = root.querySelector<HTMLElement>("#currency-shop-wallet-gems");
  const walletCash = root.querySelector<HTMLElement>("#cash-shop-wallet-cash");
  if (walletCoins) walletCoins.textContent = economy.wallet.coins.toLocaleString("en-US");
  if (walletGems) walletGems.textContent = economy.wallet.gems.toLocaleString("en-US");
  if (walletCash) walletCash.textContent = economy.wallet.cash.toLocaleString("en-US");
  return nextPages;
}

export function syncShopDetailDom(
  root: ParentNode,
  options: ShopDetailRenderOptions,
): void {
  const host = root.querySelector<HTMLElement>("#home-shop-detail-content");
  if (!host) return;
  host.innerHTML = renderShopDetailBody(options);
  patchShopDetailQuantity(root, options);
}

/** Update totals/buy state without replacing the qty input (keeps focus while typing). */
export function patchShopDetailQuantity(
  root: ParentNode,
  options: Pick<ShopDetailRenderOptions, "item" | "economy" | "quantity">,
): void {
  const host = root.querySelector<HTMLElement>("#home-shop-detail-content");
  if (!host) return;
  const { item, economy, quantity } = options;
  const unit = gmShopEffectivePrice(item);
  const maxQty = maxAffordableShopQuantity(economy, item);
  const canBuy = maxQty >= quantity && quantity >= 1;
  const totalEl = host.querySelector<HTMLElement>("#home-shop-detail-total");
  if (totalEl) {
    totalEl.textContent = (unit * quantity).toLocaleString("en-US");
  }
  const buyBtn = host.querySelector<HTMLButtonElement>("[data-shop-detail-buy]");
  if (buyBtn) buyBtn.disabled = !canBuy;
  const qtyInput = host.querySelector<HTMLInputElement>("#home-shop-detail-qty");
  if (qtyInput) {
    qtyInput.max = String(maxQty > 0 ? maxQty : 1);
  }
}
